import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowLeft,
  Bookmark,
  Check,
  ChevronDown,
  CircleX,
  Link2,
  Trash2,
  Upload,
} from 'lucide-react';
import { useCallback,useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/catalyst/badge';
import { Button } from '@/components/ui/catalyst/button';
import { Checkbox, CheckboxField } from '@/components/ui/catalyst/checkbox';
import { Field, Label } from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { MultiListbox } from '@/components/ui/catalyst/multi-listbox';
import { cn } from '@/lib/utils';
import {
  type BulkOperationTemplate,
  deleteTemplate,
  getTemplateById,
  getTemplates,
} from '@/pages/BulkAgentOperationsPage';
import { useGetFirmsQuery } from '@/services/api/firmsService';
import {
  type BulkCreateAgentChatThreadRequest,
  enhancedApi,
} from '@/services/api/generated/bulkAgentThreadsService';
import {
  useGetMatterListMetadataQuery,
  useGetMattersQuery,
} from '@/services/api/mattersService';
import { MatterStatus } from '@/services/types/client-intake-types';

import {
  type ColumnDefinition,
  ColumnDefinitionBuilder,
  PromptEditor,
} from './ColumnDefinitionBuilder';
import { parseYamlTemplate } from './yaml-template-utils';

interface SectionPanelProps {
  stepNumber: number;
  title: string;
  description: string;
  isOpen: boolean;
  onToggle: () => void;
  isComplete: boolean;
  badge?: React.ReactNode;
  children: React.ReactNode;
}

const SectionPanel: React.FC<SectionPanelProps> = ({
  stepNumber,
  title,
  description,
  isOpen,
  onToggle,
  isComplete,
  badge,
  children,
}) => (
  <div className="bg-white shadow sm:rounded-lg">
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-2.5 sm:px-5 hover:bg-zinc-50/50 transition-colors"
    >
      <div className="flex items-center gap-3">
        {isComplete ? (
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100 text-green-600">
            <Check className="h-4 w-4" />
          </span>
        ) : (
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-zinc-900 text-white text-xs font-medium">
            {stepNumber}
          </span>
        )}
        <div className="text-left">
          <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
          <p className="text-xs text-zinc-500">{description}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {badge}
        <ChevronDown
          className={cn(
            'h-4 w-4 text-zinc-500 transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
        />
      </div>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          <div className="px-4 pb-3 sm:px-5 border-t border-zinc-100">
            {children}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const BulkFinchGPTActions: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read URL params once for initial state
  const firmParam = searchParams.get('firm');
  const initialFirmParam = useRef(firmParam);
  const templateParam = searchParams.get('template');
  const initialTemplate = useMemo(
    () => (templateParam ? getTemplateById(templateParam) : undefined),
    [templateParam],
  );

  const [selectedMatters, setSelectedMatters] = useState<Set<string>>(
    new Set(),
  );
  const [title, setTitle] = useState(() => initialTemplate?.title ?? '');
  const [initialTask, setInitialTask] = useState(
    () => initialTemplate?.initialTask ?? '',
  );
  const [enableTableExtraction, setEnableTableExtraction] = useState(() => {
    if (initialTemplate?.tableDefinition?.columns) return true;
    return false;
  });
  const [columns, setColumns] = useState<ColumnDefinition[]>(() => {
    if (initialTemplate?.tableDefinition?.columns) {
      return initialTemplate.tableDefinition.columns.map((col) => ({
        ...col,
        _key: `col-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      }));
    }
    return [];
  });
  const [filters, setFilters] = useState<{
    firm?: string[];
    status?: string[];
    search?: string;
    case_manager?: string[];
    representing_attorney?: string[];
    matter_type?: string[];
  }>(() => ({
    ...(firmParam ? { firm: [firmParam] } : {}),
  }));

  // Collapsible section state
  const [isConfigOpen, setIsConfigOpen] = useState(true);
  const [isExtractionOpen, setIsExtractionOpen] = useState(() => {
    if (initialTemplate?.tableDefinition?.columns) return true;
    return false;
  });
  const [isMatterSelectionOpen, setIsMatterSelectionOpen] = useState(true);

  // Template state
  const [templates, setTemplates] = useState<BulkOperationTemplate[]>([]);
  const [isTemplateDropdownOpen, setIsTemplateDropdownOpen] = useState(false);
  const templateTriggerRef = useRef<HTMLButtonElement>(null);
  const templateDropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; right: number } | null>(null);
  const yamlFileInputRef = useRef<HTMLInputElement>(null);

  const openTemplateDropdown = useCallback(() => {
    if (templateTriggerRef.current) {
      const rect = templateTriggerRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    setIsTemplateDropdownOpen(true);
  }, []);

  // Load templates from localStorage on mount
  useEffect(() => {
    setTemplates(getTemplates());
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        templateTriggerRef.current?.contains(target) ||
        templateDropdownRef.current?.contains(target)
      ) {
        return;
      }
      setIsTemplateDropdownOpen(false);
    };
    const handleScroll = (event: Event) => {
      if (templateDropdownRef.current?.contains(event.target as Node)) {
        return;
      }
      setIsTemplateDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  // Apply a template to the form
  const applyTemplate = (template: BulkOperationTemplate) => {
    setTitle(template.title);
    setInitialTask(template.initialTask);
    if (template.tableDefinition?.columns) {
      const columnsWithKeys = template.tableDefinition.columns.map((col) => ({
        ...col,
        _key: `col-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      }));
      setColumns(columnsWithKeys);
      setEnableTableExtraction(true);
    } else {
      setColumns([]);
      setEnableTableExtraction(false);
    }
    setIsTemplateDropdownOpen(false);
    toast.success(`Template "${template.name}" applied`);
  };

  const handleYamlUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = parseYamlTemplate(content);

        if (parsed.title) setTitle(parsed.title);
        if (parsed.initialTask) setInitialTask(parsed.initialTask);
        if (parsed.enableTableExtraction) {
          setColumns(parsed.columns);
          setEnableTableExtraction(true);
        }

        toast.success(`YAML template "${file.name}" applied`);
      } catch (err) {
        toast.error('Failed to parse YAML template', {
          description:
            err instanceof Error ? err.message : 'Invalid YAML format',
        });
      }
    };
    reader.readAsText(file);

    if (yamlFileInputRef.current) {
      yamlFileInputRef.current.value = '';
    }
  };

  // Delete a template
  const handleDeleteTemplate = (
    templateId: string,
    templateName: string,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    if (window.confirm(`Delete template "${templateName}"?`)) {
      deleteTemplate(templateId);
      setTemplates(getTemplates());
      toast.success(`Template "${templateName}" deleted`);
    }
  };

  // Copy share link for a template
  const handleCopyShareLink = (
    template: BulkOperationTemplate,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    const params = new URLSearchParams();
    params.set('title', template.title);
    params.set('initialTask', template.initialTask);
    if (template.tableDefinition) {
      params.set('tableDefinition', JSON.stringify(template.tableDefinition));
    }
    const shareUrl = `${window.location.origin}/bulk-finchgpt-actions/create?${params.toString()}`;
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard', {
      description: 'Share this link to pre-fill the form with this template',
    });
  };

  // Initialize form from URL params (for duplicating existing operations)
  useEffect(() => {
    const titleParam = searchParams.get('title');
    const initialTaskParam = searchParams.get('initialTask');
    const tableDefinitionParam = searchParams.get('tableDefinition');

    if (titleParam) {
      setTitle(titleParam);
    }
    if (initialTaskParam) {
      setInitialTask(initialTaskParam);
    }
    if (tableDefinitionParam) {
      try {
        const tableDefinition = JSON.parse(tableDefinitionParam);
        if (
          tableDefinition?.columns &&
          Array.isArray(tableDefinition.columns)
        ) {
          // Add stable keys to the columns
          const columnsWithKeys = tableDefinition.columns.map(
            (col: { id: string; prompt: string }) => ({
              ...col,
              _key: `col-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            }),
          );
          setColumns(columnsWithKeys);
          setEnableTableExtraction(true);
        }
      } catch {
        // Ignore invalid JSON
      }
    }

    // Clear search params after reading them
    if (
      titleParam ||
      initialTaskParam ||
      tableDefinitionParam ||
      firmParam ||
      templateParam
    ) {
      setSearchParams(new URLSearchParams(), { replace: true });
    }
  }, []);

  const [bulkCreate, { isLoading: isCreating }] =
    enhancedApi.useBulkCreateAgentChatThreadsMutation();

  const { data: firmsData } = useGetFirmsQuery({});
  const { data: metadata } = useGetMatterListMetadataQuery();

  const {
    data: mattersData,
    isLoading,
    isFetching,
  } = useGetMattersQuery({ ...filters, limit: 1000 });

  const allMatters = mattersData?.results ?? [];
  const totalCount = mattersData?.totalCount ?? 0;
  const isResultsTruncated = totalCount > allMatters.length;

  // Section completion indicators
  const isConfigComplete =
    title.trim().length > 0 && initialTask.trim().length > 0;
  const isMatterSelectionComplete = selectedMatters.size > 0;
  const completedSections = [
    isConfigComplete,
    isMatterSelectionComplete,
  ].filter(Boolean).length;
  const totalRequiredSections = 2;

  // Statuses to exclude from auto-selection when arriving from a firm page
  const EXCLUDED_AUTO_SELECT_STATUSES: Set<string> = useMemo(
    () =>
      new Set([
        MatterStatus.INTAKE,
        MatterStatus.CASE_INGESTION,
        MatterStatus.INGESTED_IN_ERROR,
      ]),
    [],
  );

  // Auto-select all matters on first load when arriving with a firm param,
  // excluding matters with Intake, Case Ingestion, or Ingested in Error statuses
  const hasAutoSelected = useRef(false);
  useEffect(() => {
    if (initialFirmParam.current && !hasAutoSelected.current && allMatters.length > 0) {
      hasAutoSelected.current = true;
      setSelectedMatters(
        new Set(
          allMatters
            .filter((m) => !EXCLUDED_AUTO_SELECT_STATUSES.has(m.status ?? ''))
            .map((m) => m.id),
        ),
      );
    }
  }, [allMatters, EXCLUDED_AUTO_SELECT_STATUSES]);

  const toggleMatter = (matterId: string) => {
    const newSelected = new Set(selectedMatters);
    if (newSelected.has(matterId)) {
      newSelected.delete(matterId);
    } else {
      newSelected.add(matterId);
    }
    setSelectedMatters(newSelected);
  };

  const toggleAllMatters = () => {
    if (selectedMatters.size === allMatters.length) {
      setSelectedMatters(new Set());
    } else {
      setSelectedMatters(new Set(allMatters.map((m) => m.id)));
    }
  };

  const toPlainText = (value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return '';
    // If prompts are ever pasted with rich text/HTML, strip tags rather than parsing as HTML.
    const withoutTags = trimmed.replace(/<[^>]*>/g, '');
    return withoutTags
      .replace(/\r\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const handleBulkCreate = async () => {
    if (selectedMatters.size === 0) {
      toast.error('Please select at least one matter');
      return;
    }

    if (!title.trim()) {
      toast.error('Please enter a title for the threads');
      return;
    }

    if (!initialTask.trim()) {
      toast.error('Please enter an initial task/prompt');
      return;
    }

    try {
      const normalizedInitialTask = toPlainText(initialTask);

      // Filter out columns with empty prompts and strip internal UI fields
      const validColumns = columns
        .map((c) => ({
          id: c.id,
          prompt: toPlainText(c.prompt),
        }))
        .filter((c) => c.prompt.trim() !== '');

      const request: BulkCreateAgentChatThreadRequest = {
        matter_ids: Array.from(selectedMatters),
        title: title.trim(),
        initial_task: normalizedInitialTask,
        table_definition:
          enableTableExtraction && validColumns.length > 0
            ? { columns: validColumns }
            : undefined,
      };

      const result = await bulkCreate({
        bulkCreateAgentChatThreadRequest: request,
      }).unwrap();

      // The new response returns an operation_id for tracking
      toast.success(
        `Started bulk operation for ${result.total_count} matters`,
        {
          description: 'You can track progress in the operations history',
          action: {
            label: 'View Progress',
            onClick: () =>
              navigate(`/bulk-finchgpt-actions/${result.operation_id}`),
          },
          duration: 8000,
        },
      );

      // Clear selection and form
      setSelectedMatters(new Set());
      setTitle('');
      setInitialTask('');
      setColumns([]);
      setEnableTableExtraction(false);

      // Navigate to the operation detail page
      navigate(`/bulk-finchgpt-actions/${result.operation_id}`);
    } catch (error) {
      toast.error('Failed to create agent threads', {
        description: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  };

  const matterGridCols =
    'grid-cols-[2.5rem_1fr_minmax(100px,0.5fr)_minmax(100px,0.5fr)_minmax(100px,0.5fr)_minmax(100px,0.5fr)_minmax(100px,0.5fr)]';

  return (
    <div className="min-h-screen bg-zinc-50/50">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-zinc-200">
        <div className="px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/bulk-finchgpt-actions"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Link>
              <div className="h-5 w-px bg-zinc-200" />
              <div>
                <h1 className="text-sm font-semibold text-zinc-900">
                  Create Bulk Operation
                </h1>
                <p className="text-xs text-zinc-500">
                  {completedSections}/{totalRequiredSections} sections complete
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {selectedMatters.size > 0 && (
                <div className="flex items-center gap-2">
                  <Badge color="blue">
                    {selectedMatters.size} matter
                    {selectedMatters.size !== 1 ? 's' : ''} selected
                  </Badge>
                  <button
                    type="button"
                    onClick={() => setSelectedMatters(new Set())}
                    className="flex items-center gap-1 text-xs font-medium text-zinc-500 hover:text-zinc-700 transition-colors"
                  >
                    <CircleX className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              <Button
                color="blue"
                onClick={handleBulkCreate}
                disabled={
                  isCreating ||
                  selectedMatters.size === 0 ||
                  !isConfigComplete
                }
              >
                {isCreating ? 'Creating...' : 'Create Threads'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 sm:px-6 lg:px-8 py-4 space-y-3 max-w-7xl mx-auto">
        {/* Section 1: Thread Configuration */}
        <SectionPanel
          stepNumber={1}
          title="Thread Configuration"
          description="Set up the title, prompt, and template for agent threads"
          isOpen={isConfigOpen}
          onToggle={() => setIsConfigOpen(!isConfigOpen)}
          isComplete={isConfigComplete}
        >
          <div className="pt-3 space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <Field>
                  <Label>Thread Title</Label>
                  <Input
                    type="text"
                    placeholder="e.g., Case Document Analysis"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </Field>
              </div>
              <div className="flex items-center gap-2 self-end pb-0.5">
                <input
                  ref={yamlFileInputRef}
                  type="file"
                  accept=".yaml,.yml"
                  onChange={handleYamlUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => yamlFileInputRef.current?.click()}
                  className={cn(
                    'flex items-center gap-2 px-2.5 py-1 rounded-md text-xs font-medium transition-all',
                    'border border-zinc-200 bg-white hover:bg-zinc-50 hover:border-zinc-300',
                  )}
                >
                  <Upload className="h-4 w-4 text-zinc-500" />
                  <span className="text-zinc-700">Upload YAML</span>
                </button>

                {templates.length > 0 && (
                  <>
                    <button
                      ref={templateTriggerRef}
                      type="button"
                      onClick={() =>
                        isTemplateDropdownOpen
                          ? setIsTemplateDropdownOpen(false)
                          : openTemplateDropdown()
                      }
                      className={cn(
                        'flex items-center gap-2 px-2.5 py-1 rounded-md text-xs font-medium transition-all',
                        'border border-zinc-200 bg-white hover:bg-zinc-50 hover:border-zinc-300',
                        isTemplateDropdownOpen &&
                          'ring-2 ring-blue-500 border-blue-500',
                      )}
                    >
                      <Bookmark className="h-4 w-4 text-zinc-500" />
                      <span className="text-zinc-700">Load Template</span>
                      <ChevronDown
                        className={cn(
                          'h-4 w-4 text-zinc-400 transition-transform',
                          isTemplateDropdownOpen && 'rotate-180',
                        )}
                      />
                    </button>

                    {createPortal(
                      <AnimatePresence>
                        {isTemplateDropdownOpen && dropdownPos && (
                          <motion.div
                            ref={templateDropdownRef}
                            initial={{ opacity: 0, y: -4, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -4, scale: 0.95 }}
                            transition={{ duration: 0.15 }}
                            className="fixed z-50 min-w-[260px] max-w-[320px] bg-white rounded-lg shadow-lg border border-zinc-200 overflow-hidden"
                            style={{ top: dropdownPos.top, right: dropdownPos.right }}
                          >
                            <div className="max-h-80 overflow-y-auto">
                              {/* Default Templates Section */}
                              {(() => {
                                const defaultTemplates = templates.filter(t => t.isDefault);
                                const userTemplates = templates.filter(t => !t.isDefault);
                                return (
                                  <>
                                    {defaultTemplates.length > 0 && (
                                      <>
                                        <div className="px-2.5 py-1.5 border-b border-zinc-100 bg-zinc-50/80 sticky top-0 z-10">
                                          <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
                                            Default Templates
                                          </p>
                                        </div>
                                        {defaultTemplates.map((template) => (
                                          <button
                                            key={template.id}
                                            type="button"
                                            onClick={() => applyTemplate(template)}
                                            className="w-full px-2.5 py-1.5 flex items-center gap-2 hover:bg-blue-50 transition-colors group"
                                          >
                                            <div className="flex-1 text-left min-w-0 flex items-center gap-1.5">
                                              <p className="text-xs font-medium text-zinc-900 truncate">
                                                {template.name}
                                              </p>
                                              {template.id === 'default-firm-case-review' && (
                                                <span className="shrink-0 text-[9px] font-medium px-1 py-px rounded bg-blue-100 text-blue-700">
                                                  Recommended
                                                </span>
                                              )}
                                              {template.tableDefinition && (
                                                <span className="shrink-0 text-[9px] text-indigo-600 font-medium">
                                                  {template.tableDefinition.columns.length} col
                                                </span>
                                              )}
                                            </div>
                                          </button>
                                        ))}
                                      </>
                                    )}

                                    {/* User Templates Section */}
                                    {userTemplates.length > 0 && (
                                      <>
                                        <div className="px-2.5 py-1.5 border-b border-t border-zinc-100 bg-zinc-50/80 sticky top-0 z-10">
                                          <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
                                            Your Templates
                                          </p>
                                        </div>
                                        {userTemplates.map((template) => (
                                          <button
                                            key={template.id}
                                            type="button"
                                            onClick={() => applyTemplate(template)}
                                            className="w-full px-2.5 py-1.5 flex items-center gap-2 hover:bg-blue-50 transition-colors group"
                                          >
                                            <div className="flex-1 text-left min-w-0 flex items-center gap-1.5">
                                              <p className="text-xs font-medium text-zinc-900 truncate">
                                                {template.name}
                                              </p>
                                              {template.tableDefinition && (
                                                <span className="shrink-0 text-[9px] text-indigo-600 font-medium">
                                                  {template.tableDefinition.columns.length} col
                                                </span>
                                              )}
                                            </div>
                                            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-all">
                                              <button
                                                type="button"
                                                onClick={(e) => handleCopyShareLink(template, e)}
                                                className="p-1 hover:bg-blue-100 rounded transition-all"
                                                title="Copy share link"
                                              >
                                                <Link2 className="h-3 w-3 text-blue-500" />
                                              </button>
                                              <button
                                                type="button"
                                                onClick={(e) =>
                                                  handleDeleteTemplate(template.id, template.name, e)
                                                }
                                                className="p-1 hover:bg-red-100 rounded transition-all"
                                                title="Delete template"
                                              >
                                                <Trash2 className="h-3 w-3 text-red-500" />
                                              </button>
                                            </div>
                                          </button>
                                        ))}
                                      </>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>,
                      document.body,
                    )}
                  </>
                )}
              </div>
            </div>

            <Field>
              <Label>Initial Task/Prompt</Label>
              <PromptEditor
                value={initialTask}
                onChange={setInitialTask}
                placeholder="e.g., Please review all case documents and provide a summary of key findings..."
                minHeight="100px"
                maxHeight="12rem"
              />
              <p className="mt-0.5 text-xs text-zinc-500">
                This task triggers the agent to start working on each matter
              </p>
            </Field>
          </div>
        </SectionPanel>

        {/* Section 2: Structured Data Extraction */}
        <SectionPanel
          stepNumber={2}
          title="Structured Data Extraction"
          description="Define table columns for the agent to extract and populate"
          isOpen={isExtractionOpen}
          onToggle={() => {
            setIsExtractionOpen(!isExtractionOpen);
            if (!isExtractionOpen) setEnableTableExtraction(true);
          }}
          isComplete={
            !enableTableExtraction ||
            columns.some((c) => c.id.trim() && c.prompt.trim())
          }
          badge={
            enableTableExtraction && columns.length > 0 ? (
              <Badge color="indigo">
                {columns.length} column{columns.length !== 1 ? 's' : ''}
              </Badge>
            ) : undefined
          }
        >
          <div className="pt-3">
            <div className="flex items-center gap-2 mb-3">
              <CheckboxField>
                <Checkbox
                  checked={enableTableExtraction}
                  onChange={() =>
                    setEnableTableExtraction(!enableTableExtraction)
                  }
                />
              </CheckboxField>
              <span className="text-sm font-medium text-zinc-700">
                Enable structured data extraction
              </span>
            </div>

            {enableTableExtraction && (
              <ColumnDefinitionBuilder
                columns={columns}
                onChange={setColumns}
                disabled={isCreating}
              />
            )}
          </div>
        </SectionPanel>

        {/* Section 3: Matter Selection */}
        <SectionPanel
          stepNumber={3}
          title="Matter Selection"
          description="Select the matters to run agents on"
          isOpen={isMatterSelectionOpen}
          onToggle={() => setIsMatterSelectionOpen(!isMatterSelectionOpen)}
          isComplete={isMatterSelectionComplete}
          badge={
            selectedMatters.size > 0 ? (
              <Badge color="blue">{selectedMatters.size} selected</Badge>
            ) : undefined
          }
        >
          <div className="pt-3">
            <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
              {/* Filter Row */}
              <div className="flex items-center gap-2 border-b border-zinc-100 bg-zinc-50/80 px-3 py-1.5">
                <div className="flex items-center justify-center w-10 shrink-0">
                  <CheckboxField>
                    <Checkbox
                      checked={Boolean(
                        allMatters.length > 0 &&
                          selectedMatters.size === allMatters.length,
                      )}
                      indeterminate={Boolean(
                        selectedMatters.size > 0 &&
                          selectedMatters.size < allMatters.length,
                      )}
                      onChange={toggleAllMatters}
                    />
                  </CheckboxField>
                </div>
                <div className="flex-1">
                  <Input
                    type="text"
                    placeholder="Search matters..."
                    value={filters.search ?? ''}
                    onChange={(e) =>
                      setFilters({ ...filters, search: e.target.value })
                    }
                  />
                </div>
                <div className="w-32">
                  <MultiListbox
                    options={Object.entries(MatterStatus).map(
                      ([, value]) => ({
                        value,
                        label: value,
                      }),
                    )}
                    value={filters.status ?? []}
                    onChange={(values) =>
                      setFilters({
                        ...filters,
                        status: values.length > 0 ? values : undefined,
                      })
                    }
                    placeholder="All Statuses"
                  />
                </div>
                <div className="w-32">
                  <MultiListbox
                    options={
                      firmsData?.map((firm) => ({
                        value: firm.id,
                        label: firm.name,
                      })) ?? []
                    }
                    value={filters.firm ?? []}
                    onChange={(values) =>
                      setFilters({
                        ...filters,
                        firm: values.length > 0 ? values : undefined,
                      })
                    }
                    placeholder="All Firms"
                  />
                </div>
                <div className="w-32">
                  <MultiListbox
                    options={
                      metadata?.case_managers?.map((cm) => ({
                        value: cm.id,
                        label: cm.name,
                      })) ?? []
                    }
                    value={filters.case_manager ?? []}
                    onChange={(values) =>
                      setFilters({
                        ...filters,
                        case_manager:
                          values.length > 0 ? values : undefined,
                      })
                    }
                    placeholder="All Case Managers"
                  />
                </div>
                <div className="w-32">
                  <MultiListbox
                    options={
                      metadata?.attorneys?.map((attorney) => ({
                        value: attorney.id,
                        label: attorney.name,
                      })) ?? []
                    }
                    value={filters.representing_attorney ?? []}
                    onChange={(values) =>
                      setFilters({
                        ...filters,
                        representing_attorney:
                          values.length > 0 ? values : undefined,
                      })
                    }
                    placeholder="All Attorneys"
                  />
                </div>
                <div className="w-32">
                  <MultiListbox
                    options={
                      metadata?.matter_type_options?.map((opt) => ({
                        value: opt.value,
                        label: opt.label,
                      })) ?? []
                    }
                    value={filters.matter_type ?? []}
                    onChange={(values) =>
                      setFilters({
                        ...filters,
                        matter_type:
                          values.length > 0 ? values : undefined,
                      })
                    }
                    placeholder="All Case Types"
                  />
                </div>
              </div>

              {/* Column Headers */}
              <div className={`grid ${matterGridCols} gap-3 border-b border-zinc-200 bg-zinc-50 px-3 py-1.5`}>
                <div />
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                  Matter Name
                </div>
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                  Status
                </div>
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                  Firm
                </div>
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                  Case Type
                </div>
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                  Attorney
                </div>
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
                  Case Manager
                </div>
              </div>

              {/* Table Body */}
              <div className="divide-y divide-zinc-100 max-h-[480px] overflow-y-auto">
                {isLoading || isFetching ? (
                  <div className="px-4 py-12 text-center text-sm text-zinc-500">
                    Loading matters...
                  </div>
                ) : allMatters.length === 0 ? (
                  <div className="px-4 py-12 text-center text-sm text-zinc-500">
                    No matters found
                  </div>
                ) : (
                  allMatters.map((matter) => (
                    <div
                      key={matter.id}
                      className={`grid ${matterGridCols} gap-3 px-3 py-1.5 transition-colors hover:bg-zinc-50/50`}
                    >
                      <div className="flex items-center justify-center">
                        <CheckboxField>
                          <Checkbox
                            checked={selectedMatters.has(matter.id)}
                            onChange={() => toggleMatter(matter.id)}
                          />
                        </CheckboxField>
                      </div>
                      <div className="flex items-center truncate">
                        <Link
                          to={`/matters/${matter.id}`}
                          className="truncate font-medium text-zinc-900 hover:text-blue-600 transition-colors"
                          title={matter.name || 'Unnamed Matter'}
                        >
                          {matter.name || 'Unnamed Matter'}
                        </Link>
                      </div>
                      <div className="flex items-center text-sm text-zinc-600 truncate" title={matter.status || 'N/A'}>
                        {matter.status || 'N/A'}
                      </div>
                      <div className="flex items-center text-sm text-zinc-600 truncate" title={matter.firm?.name || 'N/A'}>
                        {matter.firm?.name || 'N/A'}
                      </div>
                      <div className="flex items-center text-sm text-zinc-600 truncate" title={matter.matter_type || 'N/A'}>
                        {matter.matter_type || 'N/A'}
                      </div>
                      <div className="flex items-center text-sm text-zinc-600 truncate" title={matter.representing_attorney_name || 'N/A'}>
                        {matter.representing_attorney_name || 'N/A'}
                      </div>
                      <div className="flex items-center text-sm text-zinc-600 truncate" title={matter.case_manager?.display_name || 'N/A'}>
                        {matter.case_manager?.display_name || 'N/A'}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Footer with count */}
              {allMatters.length > 0 && (
                <div className="border-t border-zinc-100 bg-zinc-50/50 px-3 py-1.5">
                  <p className="text-xs text-zinc-500">
                    {selectedMatters.size > 0 ? (
                      <>
                        <span className="font-medium text-zinc-700">
                          {selectedMatters.size}
                        </span>{' '}
                        of {allMatters.length} selected
                      </>
                    ) : (
                      <>
                        <span className="font-medium text-zinc-700">
                          {allMatters.length}
                        </span>{' '}
                        matters
                      </>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </SectionPanel>
      </div>
    </div>
  );
};
